import { create } from 'zustand';

/**
 * Presentation preferences for the patient link bar -- the single row of links that sits at the
 * top of the content column, in line with the top of the side nav, on every patient page.
 *
 * Pinned   — the bar keeps its place in the flow and is always visible.
 * Unpinned — the bar auto-hides, and reveals itself as an overlay while the pointer is near the
 *            top of the content column.  This mirrors the side nav's rail-hover behaviour, so
 *            the two halves of the chrome hide and show the same way.
 *
 * Like the side nav's own preferences these are per-browser rather than per-user, so they live
 * in localStorage.  See SidebarStore for the same pattern -- `localStorage.clear()` on sign out
 * wipes them, and every default is the visible/roomy option, so a cleared preference only ever
 * restores the fullest view rather than hiding something the user was relying on.
 */

const NAV_BAR_PINNED_KEY = 'patientNavBarPinned';
const NAV_BAR_ICON_SIZE_KEY = 'patientNavBarIconSize';

/** Both sizes show the link's label under its icon; only the icon and tile scale change. */
export type PatientNavBarIconSize = 'small' | 'large';

const readFlag = (key: string, fallback: boolean): boolean => {
  try {
    const stored = localStorage.getItem(key);
    return stored === null ? fallback : stored === 'true';
  } catch {
    return fallback;
  }
};

const writeFlag = (key: string, value: boolean) => {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    // Preference storage is best-effort; a private-mode failure must not break the bar.
  }
};

/**
 * Large is the default: the labelled tile says what each link is without a hover, which matters
 * more on first contact than the width the compact tile saves.  Small is the opt-in for a narrow
 * screen.
 */
const readIconSize = (): PatientNavBarIconSize => {
  try {
    return localStorage.getItem(NAV_BAR_ICON_SIZE_KEY) === 'small' ? 'small' : 'large';
  } catch {
    return 'large';
  }
};

export type PatientNavBarStore = {
  /** Pinned keeps the bar in the flow; unpinned hands it to hover intent. */
  isPinned: boolean;
  togglePinned: () => void;
  iconSize: PatientNavBarIconSize;
  setIconSize: (value: PatientNavBarIconSize) => void;
  /**
   * The element the bar portals into.  AppFrame owns the slot -- it is the only place that sits
   * at the top of the content column, above the breadcrumbs and level with the side nav -- while
   * the bar itself has to stay mounted inside the patient page, where its data and callbacks
   * live.  Transient, so it is deliberately not persisted.
   */
  slotEl: HTMLElement | null;
  setSlotEl: (el: HTMLElement | null) => void;
  /** Whether an unpinned bar is currently showing because the pointer is near the top. */
  isRevealed: boolean;
  setIsRevealed: (value: boolean) => void;
  /**
   * Hover intent for the bar.  One timer for all of its hover targets -- the thin sentinel strip
   * and the bar itself -- so a pointer crossing from one to the other hits one's "leave" and the
   * other's "enter" without two timers racing and blinking the bar.
   */
  scheduleReveal: (value: boolean) => void;
  cancelReveal: () => void;
};

// Matched to the side nav's own hover delays so both menus feel the same: long enough not to
// flash open on a pointer passing through, short enough not to feel stuck.
const HOVER_OPEN_DELAY_MS = 120;
const HOVER_CLOSE_DELAY_MS = 250;

let revealTimer: ReturnType<typeof setTimeout> | null = null;

const clearRevealTimer = () => {
  if (revealTimer) {
    clearTimeout(revealTimer);
    revealTimer = null;
  }
};

export const usePatientNavBarStore = create<PatientNavBarStore>(set => ({
  isPinned: readFlag(NAV_BAR_PINNED_KEY, true),
  togglePinned: () =>
    set(state => {
      writeFlag(NAV_BAR_PINNED_KEY, !state.isPinned);
      return { isPinned: !state.isPinned };
    }),
  iconSize: readIconSize(),
  setIconSize: value =>
    set(() => {
      try {
        localStorage.setItem(NAV_BAR_ICON_SIZE_KEY, value);
      } catch {
        // see writeFlag
      }
      return { iconSize: value };
    }),
  slotEl: null,
  setSlotEl: el => set({ slotEl: el }),
  isRevealed: false,
  setIsRevealed: value => {
    clearRevealTimer();
    set({ isRevealed: value });
  },
  scheduleReveal: value => {
    clearRevealTimer();
    revealTimer = setTimeout(
      () => {
        revealTimer = null;
        set({ isRevealed: value });
      },
      value ? HOVER_OPEN_DELAY_MS : HOVER_CLOSE_DELAY_MS
    );
  },
  cancelReveal: clearRevealTimer
}));
